import { TFunction } from "i18next";
import { ReviseQuoteInput } from "generated/graphql";
import { AddOnDraft, LeadQuote, ReviseQuoteDraft } from "./types";

const DEFAULT_VALIDITY_WEEKS = 2;
const DAYS_PER_WEEK = 7;

/** One item per line — sentences, so a textarea beats a tag picker here. */
export const listToLines = (list?: string[] | null): string =>
  (list ?? []).join("\n");

export const linesToList = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

/** Blank means "unset", not zero, so an empty box never prices as free. */
const toNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : null;
};

const moneyToString = (value?: number | null): string =>
  value == null ? "" : `${value}`;

let nextAddOnKey = 0;

/** A blank add-on row. The key only has to be unique within this form. */
export const emptyAddOn = (): AddOnDraft => ({
  key: `addOn-${(nextAddOnKey += 1)}`,
  label: "",
  price: "",
});

/** Rows the vendor never filled in are dropped rather than validated. */
const filledAddOns = (addOns: AddOnDraft[]): AddOnDraft[] =>
  addOns.filter((addOn) => addOn.label.trim() || addOn.price.trim());

/** Local YYYY-MM-DD for a date input. `expiresAt` arrives as epoch millis. */
const toDateInputValue = (date: Date): string => {
  const pad = (n: number) => `${n}`.padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Mirrors the server's parseValidityDays: "2 Weeks" → 14. */
const validityDays = (quoteValidFor?: string | null): number => {
  const weeks = Number(quoteValidFor?.replace(/\D/g, ""));

  return (
    (Number.isFinite(weeks) && weeks > 0 ? weeks : DEFAULT_VALIDITY_WEEKS) *
    DAYS_PER_WEEK
  );
};

/** The expiry a revision gets by default: the service's term, from today. */
export const defaultExpiry = (quoteValidFor?: string | null): string => {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + validityDays(quoteValidFor));

  return toDateInputValue(expiry);
};

/**
 * Prefills the form from the quote as issued. The expiry keeps the quote's
 * current date when it has one — the vendor is correcting a quote, not
 * necessarily restarting its clock.
 */
export const toDraft = (quote: LeadQuote): ReviseQuoteDraft => {
  const current = quote.expiresAt ? new Date(Number(quote.expiresAt)) : null;
  const hasCurrent = current != null && !Number.isNaN(current.getTime());

  return {
    chargerModel: quote.chargerModel,
    chargerCost: moneyToString(quote.chargerCost),
    addOns: quote.addOns.map((addOn) => ({
      ...emptyAddOn(),
      label: addOn.label,
      price: moneyToString(addOn.price),
    })),
    installationCost: moneyToString(quote.installationCost),
    inclusions: listToLines(quote.inclusions),
    assumptions: listToLines(quote.assumptions),
    notes: listToLines(quote.notes),
    paymentTerms: quote.paymentTerms ?? "",
    expiresAt: hasCurrent
      ? toDateInputValue(current)
      : defaultExpiry(quote.evChargerService.quoteValidFor),
    vendorNote: "",
  };
};

/**
 * What the customer will see, priced the same way the server prices it, so the
 * vendor is never shown a total the save then changes.
 */
export const draftTotals = (draft: ReviseQuoteDraft, taxRate: number) => {
  const round2 = (value: number) => Math.round(value * 100) / 100;
  const addOnTotal = filledAddOns(draft.addOns).reduce(
    (sum, addOn) => sum + (toNumber(addOn.price) ?? 0),
    0,
  );
  const subtotal = round2(
    (toNumber(draft.chargerCost) ?? 0) +
      (toNumber(draft.installationCost) ?? 0) +
      addOnTotal,
  );
  const taxAmount = round2(subtotal * taxRate);

  return { subtotal, taxAmount, total: round2(subtotal + taxAmount) };
};

/** Field-level problems the form can catch before the round trip. */
export const draftErrors = (
  draft: ReviseQuoteDraft,
  t: TFunction<"leads">,
): string[] => {
  const errors: string[] = [];
  const charger = toNumber(draft.chargerCost);
  const installation = toNumber(draft.installationCost);

  if (!draft.chargerModel.trim()) errors.push(t("revise.errors.charger_model"));
  if (charger == null || charger < 0)
    errors.push(t("revise.errors.charger_cost"));
  if (installation == null || installation < 0)
    errors.push(t("revise.errors.installation_cost"));

  filledAddOns(draft.addOns).forEach((addOn, index) => {
    const position = index + 1;
    const price = toNumber(addOn.price);

    if (!addOn.label.trim())
      errors.push(t("revise.errors.add_on_name", { position }));
    if (price == null || price < 0)
      errors.push(t("revise.errors.add_on_price", { position }));
  });

  if (!draft.expiresAt) {
    errors.push(t("revise.errors.expiry_required"));
  } else if (new Date(`${draft.expiresAt}T23:59:59`).getTime() <= Date.now()) {
    errors.push(t("revise.errors.expiry_future"));
  }

  return errors;
};

/**
 * Draft → mutation input. Call only once {@link draftErrors} is empty.
 *
 * The expiry is sent as end-of-day local time, so "valid until the 30th" means
 * through the 30th rather than the instant it began.
 */
export const toInput = (draft: ReviseQuoteDraft): ReviseQuoteInput => {
  return {
    chargerModel: draft.chargerModel.trim(),
    chargerCost: toNumber(draft.chargerCost) ?? 0,
    // An empty list is how the vendor removes every add-on.
    addOns: filledAddOns(draft.addOns).map((addOn) => ({
      label: addOn.label.trim(),
      price: toNumber(addOn.price) ?? 0,
    })),
    installationCost: toNumber(draft.installationCost) ?? 0,
    inclusions: linesToList(draft.inclusions),
    assumptions: linesToList(draft.assumptions),
    notes: linesToList(draft.notes),
    paymentTerms: draft.paymentTerms.trim() || undefined,
    expiresAt: new Date(`${draft.expiresAt}T23:59:59`).toISOString(),
    vendorNote: draft.vendorNote.trim() || undefined,
  };
};
